import { Box, Card, IconButton, Modal, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSignetStudio } from '../SignetStudioContext';
import { extractSvgAttributes, processSvgContent } from '../utils/svgUtils';
import InnerShadowFilter from './svg/InnerShadowFilter';
import TextureFilter from './svg/TextureFilter';
import OuterShadowFilter from './svg/OuterShadowFilter';
import { getConfigSwitchLabel } from './ShapeAccordion';
import { MATERIAL_TEXTURE_MAP } from '../constants';
import { useTexture } from '../../../Shared/TextureContext';

const useStyles = makeStyles((theme) => ({
  variantCard: {
    height: '100%',
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      '& $deleteButton': {
        opacity: 1,
      },
    },
  },
  materialTitle: {
    padding: theme.spacing(1),
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '1rem',
  },
  imageContainer: (props) => ({
    height: props.isLarge ? 350 : 150,
    width: props.isLarge ? 350 : 150,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    '& img, & svg': {
      maxWidth: '90%',
      maxHeight: '90%',
      objectFit: 'contain',
    },
  }),
  deleteButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    opacity: 0,
    transition: 'opacity 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.contrastText,
    },
  },
  variantInfo: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(0.75),
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'space-around',
    width: '100%',
    overflowY: 'hidden',
    overflowX: 'auto',
  },
  tag: {
    padding: theme.spacing(0.5),
    borderRadius: 12,
    fontSize: '0.7rem',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: '32%',
    display: 'inline-block',
    textAlign: 'center',
    textTransform: 'capitalize',
    width: '100%',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4),
    outline: 'none',
    maxWidth: '90vw',
    maxHeight: '90vh',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  previewContainer: {
    width: '600px',
    height: '600px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
}));

const VariantCard = ({
  mapping,
  svgFile,
  onDelete,
  isLarge = false,
  showPathPreview = true,
  className,
  isCapturingImage,
}) => {
  const classes = useStyles({ isLarge });
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const pathRef = useRef(null);
  const [svgContent, setSvgContent] = useState(null);
  const [transforms, setTransforms] = useState({
    contentTransform: '',
    pathTransform: '',
    pathDimensions: null,
    maskTransform: '',
  });
  const [showPreview, setShowPreview] = useState(false);
  const { state } = useSignetStudio();
  const [isHovered, setIsHovered] = useState(false);
  const {reversePattern} = state;
  const configSwitchLabel = getConfigSwitchLabel(state);
  const { textures } = useTexture();

  const ringImage = useMemo(() => {
    return mapping?.material?.swatch_urls?.[mapping?.shape?.sku]?.[mapping?.size?.sku]?.[mapping?.shank?.sku];
  }, [mapping]);

  const SVG_SIZE = 1200;

  const getShapeConfig = useCallback(
    (shapeSku) => {
      const config = state?.shapeConfigs?.find((config) => config.shape_sku === shapeSku);
      if (!config) return null;

      const sizeConfig = config.sizes.find((size) => size.size_sku === mapping.size?.sku);
      if (!sizeConfig) return null;

      const shankConfig = sizeConfig.shanks.find((shank) => shank.shank_sku === mapping?.shank?.sku);
      if (!shankConfig) return null;

      return {
        ...shankConfig,
        config: {
          ...shankConfig.config,
          [configSwitchLabel]: {
            scale: shankConfig.config[configSwitchLabel].scale,
            padding: shankConfig.config[configSwitchLabel].padding,
            shape_rotation: shankConfig.config[configSwitchLabel].shape_rotation,
          },
        },
        width: shankConfig.width,
        height: shankConfig.height,
        centerOffsetX: 0,
        centerOffsetY: 0,
        scaleFactor: 1,
      };
    },
    [state.shapeConfigs, mapping.size?.sku, mapping.shank?.sku, configSwitchLabel],
  );

  const shapeConfig = useMemo(() => getShapeConfig(mapping?.shape?.sku), [getShapeConfig, mapping?.shape?.sku]);

  // Load SVG content when element becomes visible and file is available
  useEffect(() => {
    if (svgFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = processSvgContent(e.target.result, mapping.color?.hexcode || '#FFF');
        setSvgContent(content);
      };
      reader.readAsText(svgFile);
    }
  }, [svgFile, mapping.color]);

  // Calculate transforms when content is loaded
  useEffect(() => {
    if (!pathRef.current || !shapeConfig || !gRef.current || !svgContent) return;

    requestAnimationFrame(() => {
      const bbox = pathRef.current.getBBox();
      if (!bbox || !isFinite(bbox.width) || !isFinite(bbox.height)) return;

      // Calculate path transform
      const maxShapeDimension = Math.max(shapeConfig.width, shapeConfig.height);
      const pathMaxDimension = Math.max(bbox.width, bbox.height);
      const pathScale = maxShapeDimension / pathMaxDimension;

      const scaledWidth = bbox.width * pathScale;
      const scaledHeight = bbox.height * pathScale;
      const translateX = (SVG_SIZE - scaledWidth) / 2 - bbox.x * pathScale;
      const translateY = (SVG_SIZE - scaledHeight) / 2 - bbox.y * pathScale;

      // Calculate mask transform with padding consideration
      let maskTransform = `translate(${translateX}, ${translateY}) scale(${pathScale})`;

      if (shapeConfig?.config[configSwitchLabel].padding > 0) {
        const centerX = bbox.x + bbox.width / 2;
        const centerY = bbox.y + bbox.height / 2;
        const paddingScale = 1 - shapeConfig?.config[configSwitchLabel].padding / 100;

        maskTransform = [
          `translate(${translateX}, ${translateY})`,
          `scale(${pathScale})`,
          `translate(${centerX}, ${centerY})`,
          `scale(${paddingScale})`,
          `translate(${-centerX}, ${-centerY})`,
        ].join(' ');
      }

      // Calculate content transform
      const contentBBox = gRef.current.getBBox();
      if (!contentBBox || !isFinite(contentBBox.width) || !isFinite(contentBBox.height)) return;

      // Additional user scale factor (like if config.scale = 20 => +20%)
      const userScaleFactor = 1 + (shapeConfig?.config[configSwitchLabel].scale || 0) / 100;

      // The real "available" size is the shape's width/height times userScaleFactor
      const availableW = shapeConfig.width * userScaleFactor;
      const availableH = shapeConfig.height * userScaleFactor;

      // Compute how much to scale the shape to fit in that area
      const shapeFitScale = Math.min(
        availableW / contentBBox.width,
        availableH / contentBBox.height
      );

      // Combine with pathScale to align with the main path in the root coordinate system
      const finalShapeScale = shapeFitScale * pathScale;

      // Center the shape in SVG_SIZE × SVG_SIZE
      const finalW = contentBBox.width * finalShapeScale;
      const finalH = contentBBox.height * finalShapeScale;

      const contentTranslateX = (SVG_SIZE - finalW) / 2 - contentBBox.x * finalShapeScale;
      const contentTranslateY = (SVG_SIZE - finalH) / 2 - contentBBox.y * finalShapeScale;

      // Calculate shape's center point for rotation
      const shapeCenterX = contentBBox.x + contentBBox.width / 2;
      const shapeCenterY = contentBBox.y + contentBBox.height / 2;
      
      // Get rotation angle from config
      const rotation = shapeConfig?.config[configSwitchLabel].shape_rotation || 0;

      setTransforms({
        pathTransform: `translate(${translateX}, ${translateY}) scale(${pathScale})`,
        contentTransform: [
          `translate(${contentTranslateX}, ${contentTranslateY})`,
          `translate(${shapeCenterX * finalShapeScale}, ${shapeCenterY * finalShapeScale})`,
          `rotate(${rotation})`,
          `translate(${-shapeCenterX * finalShapeScale}, ${-shapeCenterY * finalShapeScale})`,
          `scale(${finalShapeScale})`
        ].join(' '),
        pathDimensions: { width: scaledWidth, height: scaledHeight, scale: pathScale },
        maskTransform: maskTransform,
      });
    });
  }, [shapeConfig, svgContent, configSwitchLabel]);

  // Get rotation and position from config
  const getTransformConfig = useCallback((config) => {
    if (!config) return { rotation: 0, x: 0, y: 0 };
    return {
      rotation: config.rotation || 0,
      x: config.x || 0,
      y: config.y || 0,
    };
  }, []);

  // Calculate group transform based on hover state
  const getGroupTransform = useCallback(
    (config, isHovered) => {
      if (!config || !isHovered || !transforms.pathDimensions) return '';

      const { rotation, x, y } = getTransformConfig(config);
      return `
        rotate(${-rotation})
        translate(${x}, ${y})
        
    `;
    },
    [transforms.pathDimensions, getTransformConfig],
  );

  const handlePreviewClick = () => {
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    event.preventDefault();
    onDelete();
  };

  // Add background image pattern ID
  const backgroundPatternId = useMemo(
    () => `ring-background-${mapping?.material?.id}-${mapping?.shape?.sku}-${mapping?.size?.sku}`,
    [mapping],
  );

  const textureUrl = useMemo(() => {
    if (!textures.signet) return null;

    const textureKey = MATERIAL_TEXTURE_MAP[mapping.material.sku_code];
    // Handle material-based texture for NONE color SKU
    if (mapping.color?.sku === 'NONE' && mapping.material?.sku_code && textureKey) {
      return textures.signet?.[textureKey]?.preview_url;
    } else if (mapping.color?.sku === 'NONE' && mapping.material?.sku_code && !textureKey) {
      return textures.signet?.[mapping.material?.sku_code]?.preview_url;
    }

    // Handle regular color-based texture
    return textures.signet[mapping.color?.sku]?.preview_url;
  }, [textures.signet, mapping.color?.sku, mapping.material?.sku_code]);

  const renderSVG = (isHovered) => {
    if (!svgFile || !svgContent) return null;

    // Treat the SVG as hovered when capturing
    const isEffectivelyHovered = isHovered || isCapturingImage;

    return (
      <svg
        {...extractSvgAttributes(svgContent)}
        width="100%"
        height="100%"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          border: showPathPreview && !isEffectivelyHovered ? '1px solid #CCC' : 'none',
          padding: showPathPreview && isHovered && !isCapturingImage ? '1px' : '0',
        }}
        className={`variant-svg ${className || ''}`}
        data-shape={mapping.shape.sku}
        data-size={mapping.size.sku}
        data-color={mapping.color.sku}
        data-material={JSON.stringify({
          id: mapping.material.id,
          name: mapping.material.name,
          sku: MATERIAL_TEXTURE_MAP[mapping.material.sku_code] || mapping.material.sku_code,
        })}
        data-inlay={(state.inlayLayers && mapping.color?.sku === 'NONE') ? JSON.stringify({
          id: mapping.material.id,
          name: mapping.material.name,
          sku: MATERIAL_TEXTURE_MAP[mapping.material.sku_code] || mapping.material.sku_code,
        }) : null}
        data-shank={mapping.shank.sku}
        data-school={state.selectedEngraving?.school || ''}
        data-engraving={reversePattern ? `${state.selectedEngraving?.sku_code}REV` : state.selectedEngraving?.sku_code}
        data-layer={state.inlayLayers ? "inlay_pattern" : "pattern"}
      >
        <defs>
          <InnerShadowFilter id={`innerShadow-${mapping.key}`} showStroke={!reversePattern} shadowColor={'#000000'} strength={shapeConfig?.config[configSwitchLabel].shadow || 'standard'} />
          <OuterShadowFilter 
            id={`outerShadow-${mapping.key}`} 
            shadowColor="#000000"  
            strength={shapeConfig?.config[configSwitchLabel].depth || 'standard'}
          />
          <TextureFilter
            id={`texture-${mapping.key}`}
            textureUrl={textureUrl}
            patternType={'signet'}
          />
          {/* Only add pattern if not capturing and is hovered */}
          {isHovered && !isCapturingImage && ringImage && (
            <pattern
              id={`${backgroundPatternId}-${mapping.key}`}
              patternUnits="userSpaceOnUse"
              width={SVG_SIZE}
              height={SVG_SIZE}
            >
              <image
                href={decodeURIComponent(ringImage)}
                width={SVG_SIZE}
                height={SVG_SIZE}
                preserveAspectRatio="xMidYMid meet"
              />
            </pattern>
          )}
          <mask
            id={`mask-${mapping.key}`}
            maskUnits="userSpaceOnUse"
            maskContentUnits="userSpaceOnUse"
            x="0" y="0" width="1200" height="1200"
          >
            <rect x="0" y="0" width="1200" height="1200" fill="black" />
            <path 
              d={shapeConfig?.path}
              fill="white"
              transform={transforms.maskTransform}
              stroke="none"
            />
          </mask>
          {reversePattern && (
            <mask
              id={`mask-${mapping.key}-shape-mask`}
              maskUnits="userSpaceOnUse"
              maskContentUnits="userSpaceOnUse"
              x="0" y="0" width="1200" height="1200"
            >
              <rect x="0" y="0" width="1200" height="1200" fill="white" />
              <g>
                <g mask={shapeConfig?.config[configSwitchLabel].padding > 0 ? `url(#mask-${mapping.key})` : null}>
                  <g
                    transform={transforms.contentTransform}
                    dangerouslySetInnerHTML={{
                      __html: processSvgContent(svgContent, undefined).replace(/<svg[^>]*>|<\/svg>/g, ''),
                    }}
                    fill="black"
                  />
                </g>
              </g>
            </mask>
          )}
        </defs>
        {/* Only show background ring image if not capturing and is hovered */}
        {isHovered && !isCapturingImage && ringImage && (
          <rect width={SVG_SIZE} height={SVG_SIZE} fill={`url(#${backgroundPatternId}-${mapping.key})`} />
        )}
        {/* Modified main content group */}
        <g transform={getGroupTransform(shapeConfig, isEffectivelyHovered)} style={{ transformOrigin: 'center' }} mask={`url(#mask-${mapping.key}-shape-mask)`}>
          {/* Add reverse pattern overlay */}
          {reversePattern && (
            <g>
              <path
                d={shapeConfig?.path}
                transform={transforms.maskTransform}
                fill="#666"
                stroke="none"
                filter={`url(#texture-${mapping.key}) url(#innerShadow-${mapping.key})`}
                strokeWidth={0}
              />
            </g>
          )}
          <g transform={transforms.pathTransform}>
            <path
              ref={pathRef}
              d={shapeConfig?.path}
              width={shapeConfig?.width}
              height={shapeConfig?.height}
              stroke={showPathPreview && !isEffectivelyHovered ? '#000' : 'transparent'}
              fill="transparent"
            />
          </g>
          <g
            mask={shapeConfig?.config[configSwitchLabel].padding > 0 ? `url(#mask-${mapping.key})` : null}
          >
            <g
              ref={gRef}
              transform={transforms.contentTransform}
              filter={reversePattern ? `url(#outerShadow-${mapping.key})` : `url(#texture-${mapping.key}) url(#innerShadow-${mapping.key})`}
              dangerouslySetInnerHTML={{
                __html: svgContent.replace(/<svg[^>]*>|<\/svg>/g, ''),
              }}
              fill={"white"}
            />
          </g>
        </g>
      </svg>
    );
  };
  return (
    <>
      <Card
        className={classes.variantCard}
        onClick={handlePreviewClick}
        key={`${mapping?.shape?.sku}-${mapping?.material?.sku_code}-${mapping?.color?.sku}-${mapping?.size?.sku}-${mapping?.shank?.sku}`}
      >
        <IconButton className={classes.deleteButton} onClick={handleDelete} size="small">
          <DeleteIcon fontSize="small" />
        </IconButton>
        {isLarge && (
          <Typography variant="h6" className={classes.materialTitle}>
            {mapping.size.name}
          </Typography>
        )}
        <div
          className={classes.imageContainer}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          ref={svgRef}
        >
          {renderSVG(isHovered && !showPreview)}
        </div>
        <div className={classes.variantInfo}>
          <span className={classes.tag} title={String(mapping.color?.school_color_name || mapping.color?.name || '')}>
            {mapping.color?.sku === 'NONE' ?  String(`${mapping.color?.name}: ${mapping.material?.name}`) : String(mapping.color?.school_color_name || mapping.color?.name || '')}
          </span>
          <span className={classes.tag} title={String(mapping.shank?.name || '')}>
            {String(mapping.shank?.name || '')}
          </span>
        </div>
      </Card>
      <Modal open={showPreview} onClose={handleClosePreview} className={classes.modal}>
          <Box className={classes.modalContent}>
            <IconButton className={classes.closeButton} onClick={handleClosePreview}>
              <CloseIcon />
          </IconButton>
          <Box
            className={classes.previewContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {renderSVG(isHovered && showPreview)}
          </Box>
        </Box>
      </Modal>
    </>
  );
};
export default VariantCard;